import React, { useState, useEffect } from 'react';
import {
  Box, Button, Table, Thead, Tbody, Tr, Th, Td, IconButton,
  useDisclosure, Modal, ModalOverlay, ModalContent, ModalHeader,
  ModalBody, ModalCloseButton, ModalFooter, FormControl, FormLabel,
  Input, Textarea, Select, useToast, Badge, Flex, Text, SimpleGrid,
  Card, CardBody, Heading, Tooltip
} from '@chakra-ui/react';
import { MdEdit, MdDelete, MdAdd, MdContentCopy, MdRefresh } from 'react-icons/md';
import * as notificationService from '../../../services/notificationService';

export default function TemplateManager() {
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState(null);
  
  // Form State
  const [formData, setFormData] = useState({ 
    name: '', 
    title: '', 
    body: '', 
    type: 'info',
    category: 'general'
  });

  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setIsLoading(true);
    try {
      const data = await notificationService.getNotificationTemplates();
      setTemplates(data);
    } catch (error) {
      toast({
        title: 'Error loading templates',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    // Basic Validation
    if(!formData.name || !formData.title || !formData.body) {
        toast({ title: 'Please fill all required fields', status: 'warning' });
        return;
    }

    try {
      if (currentTemplate) {
        await notificationService.updateNotificationTemplate(currentTemplate.id, formData);
        toast({ title: 'Template updated successfully', status: 'success' });
      } else {
        await notificationService.createNotificationTemplate(formData);
        toast({ title: 'Template created successfully', status: 'success' });
      }
      onClose();
      loadTemplates();
    } catch (error) {
      toast({ 
        title: 'Error saving template', 
        description: error.message, 
        status: 'error' 
      });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this template? This action cannot be undone.')) {
      try {
        await notificationService.deleteNotificationTemplate(id);
        toast({ title: 'Template deleted', status: 'success' });
        loadTemplates();
      } catch (error) {
        toast({ title: 'Error deleting template', status: 'error' });
      }
    }
  };

  const openModal = (template = null) => {
    setCurrentTemplate(template);
    if (template) {
        setFormData({
            name: template.name,
            title: template.title,
            body: template.body,
            type: template.type,
            category: template.category || 'general'
        });
    } else {
        setFormData({ name: '', title: '', body: '', type: 'info', category: 'general' });
    }
    onOpen();
  };

  const insertVariable = (variable) => {
    setFormData(prev => ({ ...prev, body: prev.body + ` {${variable}} ` }));
  };

  const getTypeColor = (type) => {
      switch(type) {
          case 'critical': return 'red';
          case 'warning': return 'orange';
          case 'success': return 'green';
          default: return 'blue';
      }
  };

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <Flex justify="space-between" mb={6} align="center" wrap="wrap" gap={4}>
        <Box>
            <Heading size="lg" mb={1}>Template Manager</Heading>
            <Text color="gray.500">Manage reusable notification messages</Text>
        </Box>
        <Flex gap={2}>
            <Button leftIcon={<MdRefresh />} onClick={loadTemplates} isLoading={isLoading} variant="ghost">
                Refresh
            </Button>
            <Button leftIcon={<MdAdd />} colorScheme="brand" onClick={() => openModal()}>
                New Template
            </Button>
        </Flex>
      </Flex>

      <Card bg="white" borderRadius="20px" boxShadow="sm" overflowX={{ sm: "scroll", xl: "hidden" }}>
        <CardBody p={0}>
            <Table variant="simple">
            <Thead bg="gray.50">
                <Tr>
                <Th>Internal Name</Th>
                <Th>Subject / Title</Th>
                <Th>Type</Th>
                <Th>Category</Th>
                <Th textAlign="right">Actions</Th>
                </Tr>
            </Thead>
            <Tbody>
                {templates.length === 0 && !isLoading ? (
                    <Tr><Td colSpan={5} textAlign="center" py={8} color="gray.500">No templates found. Create one to get started.</Td></Tr>
                ) : (
                    templates.map((tpl) => (
                    <Tr key={tpl.id} _hover={{ bg: "gray.50" }}>
                        <Td fontWeight="600">{tpl.name}</Td>
                        <Td maxW="300px" isTruncated>{tpl.title}</Td>
                        <Td>
                            <Badge colorScheme={getTypeColor(tpl.type)} variant="subtle" borderRadius="full" px={2}>
                                {tpl.type.toUpperCase()}
                            </Badge>
                        </Td>
                        <Td textTransform="capitalize">{tpl.category}</Td>
                        <Td textAlign="right">
                        <Tooltip label="Edit Template">
                            <IconButton 
                                icon={<MdEdit />} 
                                size="sm" 
                                mr={2} 
                                colorScheme="blue"
                                variant="ghost"
                                onClick={() => openModal(tpl)} 
                            />
                        </Tooltip>
                        <Tooltip label="Delete Template">
                            <IconButton 
                                icon={<MdDelete />} 
                                size="sm" 
                                colorScheme="red" 
                                variant="ghost"
                                onClick={() => handleDelete(tpl.id)} 
                            />
                        </Tooltip>
                        </Td>
                    </Tr>
                    ))
                )}
            </Tbody>
            </Table>
        </CardBody>
      </Card>

      {/* Create/Edit Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{currentTemplate ? 'Edit Template' : 'Create New Template'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <SimpleGrid columns={2} spacing={4} mb={4}>
                <FormControl isRequired>
                <FormLabel>Internal Name</FormLabel>
                <Input 
                    value={formData.name} 
                    onChange={(e) => setFormData({...formData, name: e.target.value})} 
                    placeholder="e.g., Monthly Bill Reminder"
                />
                </FormControl>

                <FormControl>
                <FormLabel>Category</FormLabel>
                <Select value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})}>
                    <option value="general">General</option>
                    <option value="service">Service Alert</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="billing">Billing</option>
                    <option value="emergency">Emergency</option>
                </Select>
                </FormControl>
            </SimpleGrid>

            <FormControl mb={4}>
                <FormLabel>Type / Priority</FormLabel>
                <Select value={formData.type} onChange={(e) => setFormData({...formData, type: e.target.value})}>
                    <option value="info">Info (Normal)</option>
                    <option value="warning">Warning (High)</option>
                    <option value="critical">Critical (Urgent)</option>
                    <option value="success">Success (Positive)</option>
                </Select>
            </FormControl>

            <FormControl mb={4} isRequired>
              <FormLabel>Subject / Title</FormLabel>
              <Input 
                value={formData.title} 
                onChange={(e) => setFormData({...formData, title: e.target.value})} 
                placeholder="Notification Title (seen by user)"
              />
            </FormControl>

            <FormControl mb={4} isRequired>
              <Flex justify="space-between" align="center" mb={2}>
                <FormLabel mb={0}>Body Content</FormLabel>
                <Box>
                  <Text fontSize="xs" display="inline" mr={2} color="gray.500">Insert Variable:</Text>
                  {['area', 'date', 'time', 'amount', 'userName'].map(v => (
                    <Badge 
                        key={v} 
                        mr={1} 
                        cursor="pointer" 
                        colorScheme="gray" 
                        variant="outline"
                        onClick={() => insertVariable(v)}
                        _hover={{ bg: "gray.100" }}
                    >
                        {v}
                    </Badge>
                  ))}
                </Box>
              </Flex>
              <Textarea 
                value={formData.body} 
                onChange={(e) => setFormData({...formData, body: e.target.value})} 
                placeholder="Dear {userName}, water service in {area} will be..."
                rows={6}
              />
              <Text fontSize="xs" color="gray.500" mt={1}>
                Use variables like {'{area}'} to create dynamic messages.
              </Text>
            </FormControl>
          </ModalBody>

          <ModalFooter bg="gray.50" borderRadius="0 0 6px 6px">
            <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
            <Button colorScheme="brand" onClick={handleSave}>
                {currentTemplate ? 'Save Changes' : 'Create Template'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}